import { AuthBaseClient } from '../base-client';
import type { SearchResponse } from '@vagusx/shared';
import type {
  AgentSummaryResponse,
  ResolvedAgentConfigResponse,
  BaseConfigSummaryResponse,
  CreateAgentRequest,
  UpdateAgentRequest,
  DuplicateAgentRequest,
  AgentSearchRequest,
} from './types';

/**
 * Client for agent API endpoints.
 */
export class AgentClient extends AuthBaseClient {
  /**
   * Get all agents for the organization.
   */
  async getAll(): Promise<AgentSummaryResponse[]> {
    return this.get<AgentSummaryResponse[]>('/api/agents');
  }

  /**
   * Get agent by ID (resolved configuration).
   */
  async getById(id: string): Promise<ResolvedAgentConfigResponse> {
    return this.get<ResolvedAgentConfigResponse>(`/api/agents/${id}`);
  }

  /**
   * Get raw agent record (for editing).
   */
  async getRaw(id: string): Promise<Record<string, unknown>> {
    return this.get<Record<string, unknown>>(`/api/agents/${id}/raw`);
  }

  /**
   * Search agents with filters.
   */
  async search(request: AgentSearchRequest): Promise<SearchResponse<AgentSummaryResponse>> {
    return this.post<SearchResponse<AgentSummaryResponse>>('/api/agents/search', request);
  }

  /**
   * Get available base configurations.
   */
  async getBaseConfigs(): Promise<BaseConfigSummaryResponse[]> {
    return this.get<BaseConfigSummaryResponse[]>('/api/agents/base-configs');
  }

  /**
   * Get base configurations for a specific channel.
   */
  async getBaseConfigsByChannel(channel: string): Promise<BaseConfigSummaryResponse[]> {
    return this.get<BaseConfigSummaryResponse[]>(`/api/agents/base-configs/${channel}`);
  }

  /**
   * Create a new agent.
   */
  async create(data: CreateAgentRequest): Promise<AgentSummaryResponse> {
    return this.post<AgentSummaryResponse>('/api/agents', data);
  }

  /**
   * Update an agent.
   */
  async update(id: string, data: UpdateAgentRequest): Promise<AgentSummaryResponse> {
    return this.patch<AgentSummaryResponse>(`/api/agents/${id}`, data);
  }

  /**
   * Delete an agent (soft delete).
   */
  async deleteAgent(id: string): Promise<void> {
    return this.delete<void>(`/api/agents/${id}`);
  }

  /**
   * Duplicate an agent with a new name.
   */
  async duplicate(id: string, data: DuplicateAgentRequest): Promise<AgentSummaryResponse> {
    return this.post<AgentSummaryResponse>(`/api/agents/${id}/duplicate`, data);
  }

  /**
   * Toggle agent active status.
   */
  async toggleActive(id: string): Promise<AgentSummaryResponse> {
    return this.post<AgentSummaryResponse>(`/api/agents/${id}/toggle-active`, {});
  }
}
